<template>
  <Stack
    class="tw-w-[700px] tw-max-w-full"
    gap="4"
  >
    <Title>
      {{ note ? 'Edit' : 'New' }} Note
    </Title>

    <TextInput
      v-model="title"
      placeholder="Title (Optional)"
    />

    <textarea
      v-model="content"
      class="stk-input tw-h-64"
      placeholder="Jot something down about this person"
      required
    />

    <p class="tw-text-sm tw-text-gray-500">
      Markdown supported: **bold**, *italic*, # headings, - lists, etc.
    </p>

    <div class="tw-flex tw-gap-3">
      <Button
        :disabled="content.trim() === ''"
        :loading="loading"
        variant="primary"
        class="tw-grow tw-basis-0"
        @click="note ? updateNote() : createNote()"
      >
        Save
      </Button>

      <Button
        :disabled="loading"
        variant="secondary"
        class="tw-grow tw-basis-0"
        @click="emit('close')"
      >
        Cancel
      </Button>
    </div>
  </Stack>
</template>


<script setup>
import { ref, watch } from 'vue';
import { fetchWrapper } from '@frontend/shared/fetch-wrapper.js';
import { Button, Stack, TextInput, Title } from '@stakeui';

import { useAdvisorStore } from '@frontend/ui/stores/advisorStore.js';
const advisorStore = useAdvisorStore();


const props = defineProps({
  entityExternalId: { type: String, required: true },
  note: { type: Object, required: false, default: null },
});


const emit = defineEmits(['refresh', 'close']);


const title = ref(props.note ? props.note.title : '');
const content = ref(props.note ? props.note.content : '');
const loading = ref(false);


watch(
    () => props.note,
    newNote => {
      if (newNote) {
        title.value = newNote.title;
        content.value = newNote.content;
      } else {
        title.value = '';
        content.value = '';
      }
    },
    { immediate: true },
);


const clear = () => {
  title.value = '';
  content.value = '';
};


const createNote = async () => {
  const data = {
    content: content.value,
  };

  if (title.value) {
    data.title = title.value;
  }

  loading.value = true;

  try {
    await fetchWrapper(`/api/v1/entities/${props.entityExternalId}/notes/`, {
      method: 'POST',
      headers: advisorStore.headers,
      body: JSON.stringify(data),
    });

    emit('refresh');
    clear();
  } catch (err) {
    console.error('Failed to create note:', err);
  } finally {
    loading.value = false;
  }
};


const updateNote = async () => {
  if (loading.value) return;
  loading.value = true;

  const data = {
    content: content.value,
  };

  if (title.value) {
    data.title = title.value;
  }

  try {
    await fetchWrapper(`/api/v1/notes/${props.note.external_id}/`, {
      method: 'PATCH',
      headers: advisorStore.headers,
      body: JSON.stringify(data),
    });

    emit('refresh');
  } catch (err) {
    console.error('Failed to update note:', err);
  } finally {
    loading.value = false;
  }
};

</script>
